import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import {
  Button,
  Card,
  CardHeader,
  CardTitle,
  FormField,
  Input,
} from '@/components/ui';
import { getErrorMessage } from '@/api';
import { SALES_PLATFORMS } from '@/api/admin';
import type {
  AdminDecorationMethod,
  AdminProductPosition,
  ProductDecorationMethodPrice,
  SalesPlatform,
} from '@/api/admin';
import {
  useAdminDecorationMethods,
  useSetProductDecorationPrice,
} from '@/hooks/useAdminDecorationMethods';
import { isValidPrice } from '@/lib/decoration-price';

const PLATFORM_LABEL: Record<SalesPlatform, string> = {
  DIRECT_SALE: 'Direct Sale',
  SELLER_CREATOR: 'Seller Creator',
};

interface ProductMethodPriceOverridesProps {
  productId: string;
  positions: AdminProductPosition[] | undefined;
  overrides: ProductDecorationMethodPrice[] | undefined;
}

/**
 * Product-wide decoration price overrides, per method and platform.
 *
 * Lives outside the position tabs on purpose: overrides are product-scoped,
 * and showing them inside a position tab would read as per-position pricing.
 * Only methods assigned to at least one position are editable; an override
 * whose method lost its last assignment stays visible but greyed out rather
 * than silently living on.
 */
export function ProductMethodPriceOverrides({
  productId,
  positions,
  overrides,
}: ProductMethodPriceOverridesProps) {
  const { data: catalog, isLoading, isError } = useAdminDecorationMethods();

  const assignedIds = new Set(
    (positions ?? []).flatMap((position) =>
      (position.decorationMethods ?? []).map((a) => a.decorationMethodId)
    )
  );
  const overriddenIds = new Set(
    (overrides ?? []).map((row) => row.decorationMethodId)
  );

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Decoration prices</CardTitle>
        </CardHeader>
        <p className="text-xs text-mute">Loading catalog…</p>
      </Card>
    );
  }

  if (isError || !catalog) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Decoration prices</CardTitle>
        </CardHeader>
        <p className="text-xs text-danger">
          Could not load the decoration method catalog.
        </p>
      </Card>
    );
  }

  // Assigned methods first, then orphaned overrides so nothing disappears.
  const rows = catalog.filter(
    (method) => assignedIds.has(method.id) || overriddenIds.has(method.id)
  );

  return (
    <Card>
      <CardHeader>
        <div>
          <CardTitle>Decoration prices</CardTitle>
          <p className="mt-1 text-sm text-ink-2">
            Product-specific overrides. Without one, the base price applies.
          </p>
        </div>
      </CardHeader>

      {rows.length === 0 ? (
        <p className="text-sm text-mute">
          No decoration methods assigned to this product yet.
        </p>
      ) : (
        <div className="space-y-6">
          {rows.map((method) => {
            const isOrphaned = !assignedIds.has(method.id);
            return (
              <div key={method.id} className="space-y-3">
                <div className="flex flex-wrap items-baseline gap-2">
                  <span
                    className={
                      isOrphaned
                        ? 'text-sm text-mute'
                        : 'text-sm font-medium text-ink'
                    }
                  >
                    {method.name}
                  </span>
                  <span className="font-mono text-xs text-mute">
                    {method.code}
                  </span>
                  {isOrphaned && (
                    <span className="rounded-chip bg-warning px-1.5 py-0.5 text-xs text-ink">
                      inactive — method not assigned to any position
                    </span>
                  )}
                </div>

                <div className="grid gap-3 sm:grid-cols-2">
                  {SALES_PLATFORMS.map((platform) => (
                    <OverrideField
                      key={platform}
                      productId={productId}
                      method={method}
                      platform={platform}
                      override={
                        (overrides ?? []).find(
                          (row) =>
                            row.decorationMethodId === method.id &&
                            row.platform === platform
                        ) ?? null
                      }
                      disabled={isOrphaned}
                    />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}

interface OverrideFieldProps {
  productId: string;
  method: AdminDecorationMethod;
  platform: SalesPlatform;
  override: ProductDecorationMethodPrice | null;
  disabled: boolean;
}

/**
 * One method/platform price cell.
 *
 * Removing an override only happens through the explicit reset action, never
 * by emptying the field: `type="number"` reports unparseable input (a typed
 * "5,46", an intermediate "5.") as an empty string, so a Save on empty would
 * delete the override the admin meant to change. Entering 0 stays a real
 * override (free decoration).
 */
function OverrideField({
  productId,
  method,
  platform,
  override,
  disabled,
}: OverrideFieldProps) {
  const toInput = (v: string | null | undefined) =>
    v === null || v === undefined ? '' : Number(v).toFixed(2);

  const [draft, setDraft] = useState(() => toInput(override?.price));
  const { mutateAsync, isPending } = useSetProductDecorationPrice(productId);

  useEffect(() => {
    setDraft(toInput(override?.price));
  }, [method.id, platform, override?.price]);

  const basePrice =
    method.platformPrices.find((row) => row.platform === platform)?.price ??
    null;
  const persisted = toInput(override?.price);
  const isDirty = draft.trim() !== persisted;
  const parsed = draft.trim() === '' ? null : Number(draft);
  const isValid = parsed !== null && isValidPrice(parsed);
  const inputId = `override-${method.id}-${platform}`;

  const save = async (price: number | null) => {
    try {
      await mutateAsync({ methodId: method.id, platform, price });
      toast.success(price === null ? 'Reset to base price' : 'Override saved');
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const showError = isDirty && !isValid;

  return (
    <FormField
      label={PLATFORM_LABEL[platform]}
      htmlFor={inputId}
      error={
        showError
          ? parsed === null
            ? 'Enter an amount, or use “Reset to base price”.'
            : 'Enter 0 or a positive amount with at most two decimals.'
          : undefined
      }
    >
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Input
            id={inputId}
            type="number"
            min={0}
            step="0.01"
            inputMode="decimal"
            value={draft}
            disabled={disabled}
            placeholder={basePrice ?? 'unpriced'}
            onChange={(e) => setDraft(e.target.value)}
            className="pr-8"
          />
          <span className="pointer-events-none absolute right-3.5 top-1/2 -translate-y-1/2 text-xs text-mute">
            €
          </span>
        </div>
        <Button
          onClick={() => parsed !== null && isValid && save(parsed)}
          disabled={disabled || !isDirty || !isValid || isPending}
        >
          {isPending ? 'Saving…' : 'Save'}
        </Button>
      </div>
      {!showError && (
        <div className="flex items-center gap-2">
          <p className="text-xs text-mute">
            Base: {basePrice !== null ? `${basePrice} €` : 'unpriced'}
          </p>
          {override && !disabled && (
            <button
              type="button"
              onClick={() => save(null)}
              disabled={isPending}
              className="text-xs font-medium text-primary hover:underline disabled:text-mute"
            >
              Reset to base price
            </button>
          )}
        </div>
      )}
    </FormField>
  );
}
